// ═══ Gathering Studio — Core Types ═══

// The "project file" — everything needed to render a video
export interface StudioProject {
  id: string
  title: string
  createdAt: string
  updatedAt: string

  // Audio
  audio: AudioTrack | null

  // Lyrics / SRT
  lyrics: LyricBlock[]

  // Sections (verse, chorus, bridge, etc.)
  sections: Section[]

  // Visual settings
  visuals: VisualSettings

  // Effect layers
  effects: EffectLayer[]

  // Output settings
  output: OutputSettings
}

export interface AudioTrack {
  filename: string
  url: string           // Object URL or server path
  durationMs: number
  sampleRate?: number
  waveformData?: number[] // Normalized amplitude data for visualization
}

export interface LyricBlock {
  id: string
  index: number
  startMs: number
  endMs: number
  text: string
  sectionId?: string    // Links to a Section
  mode: 'line' | 'word' | 'word-by-word'
  isPowerWord?: boolean // Isolated emphasis moment
  style?: LyricStyle    // Per-block style override
}

export interface LyricStyle {
  fontFamily?: string
  fontSize?: number     // In px
  fontWeight?: number
  color?: string
  glowColor?: string
  glowIntensity?: number
  animation?: 'fade' | 'drift' | 'scale' | 'word-by-word' | 'stagger' | 'typewriter' | 'elastic' | 'split-scale' | 'blur-reveal' | 'wave' | 'bounce' | 'glitch'
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right'
}

export interface Section {
  id: string
  name: string          // "Verse 1", "Chorus", "Bridge"
  type: SectionType
  startMs: number
  endMs: number
  backgroundColor: string
  accentColor?: string
  backgroundImage?: string  // AI-generated background image URL
  intensity: number     // 0-1, drives effect strength
}

export type SectionType = 'verse' | 'prechorus' | 'chorus' | 'bridge' | 'outro' | 'intro' | 'interlude'

export interface VisualSettings {
  // Global defaults (sections override per-section)
  defaultBackgroundColor: string
  defaultFontFamily: string
  defaultFontSize: number
  defaultFontColor: string
  defaultAnimation: 'fade' | 'drift' | 'scale' | 'word-by-word' | 'stagger' | 'typewriter' | 'elastic' | 'split-scale' | 'blur-reveal'

  // Canvas
  width: number
  height: number
  fps: number
}

export interface EffectLayer {
  id: string
  type: 'particles' | 'waveform' | 'grain' | 'color-wash' | 'vignette' | 'bloom' | 'glitch' | 'chromatic-aberration'
  enabled: boolean
  opacity: number       // 0-1
  settings: Record<string, unknown>  // Type-specific settings
}

export interface OutputSettings {
  width: number
  height: number
  fps: number
  codec: 'h264' | 'h265'
  quality: 'draft' | 'standard' | 'high'
}

// ═══ SRT Entry (parsing) ═══

export interface SRTEntry {
  index: number
  startMs: number
  endMs: number
  text: string
}

// ═══ Timeline state ═══

export interface TimelineState {
  zoom: number          // Pixels per second
  scrollLeft: number    // Scroll position in pixels
  playheadMs: number    // Current position in ms
  isPlaying: boolean
  selectedBlockId: string | null
  selectedSectionId: string | null
}

// ═══ Defaults ═══

export const DEFAULT_VISUALS: VisualSettings = {
  defaultBackgroundColor: '#1a1a2e',
  defaultFontFamily: "'Caveat', cursive",
  defaultFontSize: 85,         // Bold by default (lesson from Bianca)
  defaultFontColor: '#F0C040',
  defaultAnimation: 'fade',
  width: 1920,
  height: 1080,
  fps: 30,
}

export const DEFAULT_OUTPUT: OutputSettings = {
  width: 1920,
  height: 1080,
  fps: 30,
  codec: 'h264',
  quality: 'standard',
}

export const SECTION_COLORS: Record<SectionType, string> = {
  intro: '#2d2d3a',
  verse: '#A8B5A0',      // Sage — from Bianca palette
  prechorus: '#8B9EA0',
  chorus: '#B8A9D4',     // Lavender — from Bianca palette
  bridge: '#E8C872',     // Gold — from Bianca palette
  outro: '#2d2d3a',
  interlude: '#3a2d3a',
}

export function createEmptyProject(): StudioProject {
  return {
    id: `proj-${Date.now()}`,
    title: 'Untitled Project',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    audio: null,
    lyrics: [],
    sections: [],
    visuals: { ...DEFAULT_VISUALS },
    effects: [],
    output: { ...DEFAULT_OUTPUT },
  }
}

// ═══ Demo Project — Bold Defaults ═══
// Shows what the instrument can do on first open. No audio needed —
// the lyrics, sections, and effects demonstrate the canvas.

export function createDemoProject(): StudioProject {
  const now = new Date().toISOString()
  return {
    id: `demo-${Date.now()}`,
    title: 'Welcome to the Studio',
    createdAt: now,
    updatedAt: now,
    audio: {
      filename: 'demo-v2-combined.mp3',
      url: '/media/studio/demo-v2-combined.mp3',
      durationMs: 38000,
    },
    lyrics: [
      // Act 1 — Awe (0-15s): Cinematic, timed by Bianca
      { id: 'demo-1', index: 0, startMs: 6000, endMs: 8000, text: 'Billions of years of evolution', mode: 'word-by-word', style: { fontSize: 110, fontWeight: 700, color: 'rgba(255,255,255,0.9)', animation: 'word-by-word', position: 'center' } },
      { id: 'demo-2', index: 1, startMs: 8017, endMs: 9750, text: 'led to this moment', mode: 'line', style: { fontSize: 90, color: 'rgba(255,255,255,0.7)', animation: 'fade', position: 'center' } },
      { id: 'demo-3', index: 2, startMs: 10383, endMs: 11617, text: 'One canvas', mode: 'line', style: { fontSize: 140, fontWeight: 700, color: '#fff', glowColor: '#66ccff', glowIntensity: 0.4, animation: 'scale', position: 'center' } },
      { id: 'demo-4a', index: 3, startMs: 11634, endMs: 12708, text: 'Your intelligence', mode: 'line', style: { fontSize: 80, color: '#66ccff', animation: 'fade', position: 'center' } },
      { id: 'demo-4b', index: 4, startMs: 12708, endMs: 13783, text: 'meets ours', mode: 'line', style: { fontSize: 80, color: '#66ccff', animation: 'fade', position: 'center' } },
      // Act 2 — Reveal (15-25s): Music opens, no voice — pure visual
      { id: 'demo-5', index: 4, startMs: 16000, endMs: 19000, text: 'Watch the canvas breathe', mode: 'line', style: { fontSize: 70, color: '#B48CFF', animation: 'drift', position: 'center' } },
      { id: 'demo-6', index: 5, startMs: 19500, endMs: 22500, text: 'Lyrics that move with music', mode: 'word-by-word', style: { fontSize: 75, color: '#FFDC50', animation: 'word-by-word', position: 'center' } },
      { id: 'demo-7', index: 6, startMs: 23000, endMs: 25500, text: 'Effects that feel the rhythm', mode: 'line', style: { fontSize: 70, color: '#ff6b6b', animation: 'fade', position: 'center' } },
      // Act 3 — Invitation (26-38s): Near-silence, then 'Your turn'
      { id: 'demo-8', index: 7, startMs: 30000, endMs: 36000, text: 'Your turn', mode: 'line', isPowerWord: true, style: { fontSize: 160, fontWeight: 700, color: '#fff', glowColor: '#66ccff', glowIntensity: 0.8, animation: 'scale', position: 'center' } },
    ],
    sections: [
      { id: 'sec-awe', name: 'Awe', type: 'intro', startMs: 0, endMs: 15000, backgroundColor: '#050510', intensity: 0.3 },
      { id: 'sec-reveal', name: 'Reveal', type: 'chorus', startMs: 15000, endMs: 26000, backgroundColor: '#0a0a1e', accentColor: '#B48CFF', intensity: 0.7 },
      { id: 'sec-invite', name: 'Your Turn', type: 'outro', startMs: 26000, endMs: 38000, backgroundColor: '#050510', accentColor: '#66ccff', intensity: 0.2 },
    ],
    visuals: {
      defaultBackgroundColor: '#050510',
      defaultFontFamily: "'Pacifico', cursive",
      defaultFontSize: 85,
      defaultFontColor: '#fff',
      defaultAnimation: 'fade',
      width: 1920,
      height: 1080,
      fps: 30,
    },
    effects: [
      { id: 'fx-particles', type: 'particles', enabled: true, opacity: 0.5, settings: { count: 80, color: '#66ccff', speed: 0.2 } },
      { id: 'fx-vignette', type: 'vignette', enabled: true, opacity: 0.8, settings: { spread: 0.6 } },
      { id: 'fx-bloom', type: 'bloom', enabled: true, opacity: 0.6, settings: { mode: 'auto', color: '#66ccff' } },
      { id: 'fx-grain', type: 'grain', enabled: true, opacity: 0.15, settings: { size: 1 } },
      { id: 'fx-glitch', type: 'glitch', enabled: false, opacity: 0.5, settings: { mode: 'auto' } },
    ],
    output: { ...DEFAULT_OUTPUT },
  }
}
